import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
  type UserCredential
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import i18n from 'i18next';

interface UserData {
  role: 'admin' | 'business' | 'worker' | 'employee';
  businessId?: string;
  language?: string;
  proyectosAsignados?: string[];
  tareasAsignadas?: any[];
  businessesAsignados?: string[];
  status?: string;
}

interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  signUp: (email: string, password: string) => Promise<UserCredential>;
  signIn: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[AuthContext] Auth state changed:', user?.uid, user?.email);
      setCurrentUser(user);
      if (user) {
        try {
          console.log('[AuthContext] Attempting to fetch user data for:', user.uid);
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          console.log('[AuthContext] User document exists:', userDoc.exists());
          if (userDoc.exists()) {
            const data = userDoc.data() as UserData;
            console.log('[AuthContext] User data loaded:', data);
            setUserData(data);
            if (data.language && i18n.language !== data.language) {
              // Usar setTimeout para evitar el error de actualización durante el render
              setTimeout(() => {
                try {
                  i18n.changeLanguage(data.language);
                } catch (error) {
                  console.error('[AuthContext] Error changing language:', error);
                }
              }, 0);
            }
          } else {
            console.log('[AuthContext] User document does not exist in Firestore');
            // Crear un usuario temporal para evitar errores
            setUserData({
              role: 'business', // Rol por defecto
              language: 'es'
            });
          }
                  } catch (error) {
            console.error('[AuthContext] Error loading user data:', error);
            // Si hay un error de permisos, intentar crear el documento del usuario
            if (error instanceof Error && error.message.includes('permission-denied')) {
              console.log('[AuthContext] Permission denied - user may not exist in Firestore');
              // Crear un usuario temporal para evitar errores
              setUserData({
                role: 'business', // Rol por defecto
                language: 'es'
              });
            } else {
              // Para otros errores, también crear un usuario temporal
              console.log('[AuthContext] Other error - creating temporary user data');
              setUserData({
                role: 'business', // Rol por defecto
                language: 'es'
              });
            }
          }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [db]);

  const signUp = async (email: string, password: string) => {
    return await createUserWithEmailAndPassword(auth, email, password);
  };

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const value = {
    currentUser,
    userData,
    signUp,
    signIn,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 